import React from 'react'
import clsx from 'clsx'

import { Button, Tooltip } from '../../atoms'

import styles from './_.module.css'

export interface SearchListItem {
  key: string
  list: {
    header: string
    subHeader?: string
    id: string | number
  }[]
}

type AddItemProps =
  | {
      shouldShowAddItemButton: true
      submitButtonText: string
      plusImage: React.ReactElement
      handleSubmitClick: () => void
    }
  | {
      shouldShowAddItemButton?: false
      submitButtonText?: string
      plusImage?: React.ReactElement
      handleSubmitClick?: () => void
    }

type ViewItemProps =
  | {
      shouldShowViewItemButton: true
      viewItemButtonText?: string
      onViewItemClick: (itemId: string) => void
    }
  | {
      shouldShowViewItemButton?: false
      viewItemButtonText?: string
      onViewItemClick?: (itemId: string) => void
    }

interface BaseProps {
  list: SearchListItem[]
  listImage: React.ReactElement
  handleListItemClick: (val: string | number, details?: string) => void
  emptyItemImage: React.ReactElement
  imgHeaderText: string
  imgBottomText: string
  width?: number
  selectedItemId?: string
}

type TemplateListProps = BaseProps & AddItemProps & ViewItemProps

export const TemplateList: React.FC<TemplateListProps> = ({
  list,
  listImage,
  handleListItemClick,
  emptyItemImage,
  imgHeaderText,
  imgBottomText,
  width,
  selectedItemId,
  shouldShowAddItemButton = true,
  submitButtonText,
  plusImage,
  handleSubmitClick,
  shouldShowViewItemButton = false,
  viewItemButtonText = 'View Template',
  onViewItemClick
}) => {
  return (
    <div>
      <div>
        <div className={clsx(styles.scrollableList, 'scrollableList')}>
          {list.reduce((prev, curr) => prev + curr.list.length, 0) === 0 && (
            <div className={styles.emptyIconContainer}>
              <div className={styles.emptyIcon}>{emptyItemImage}</div>
              <h2>{imgHeaderText}</h2>
              <p>{imgBottomText}</p>
            </div>
          )}
          {list?.map((listItem, listItemIndex) => {
            return (
              listItem?.list?.length > 0 && (
                <div
                  key={listItemIndex}
                  className={styles.templateListHeaderWrap}
                >
                  <span className={styles.templatePopoverContainerHeader}>
                    {listItem?.key}
                  </span>
                  {listItem?.list.map((listSubItem, listSubItemIndex) => {
                    const isActiveItem = listSubItem?.id === selectedItemId
                    return (
                      <div
                        key={listSubItemIndex}
                        className={clsx(styles.templatePopoverItems, {
                          [styles.activeTemplate]: isActiveItem,
                          activeTemplate: isActiveItem
                        })}
                        role="button"
                        tabIndex={listSubItemIndex}
                        onClick={() =>
                          handleListItemClick(
                            listSubItem?.id,
                            listSubItem?.header
                          )
                        }
                        onKeyPress={() => handleListItemClick(listSubItem?.id)}
                      >
                        {listImage}
                        <div className={styles.templateNameCreatorWrap}>
                          <Tooltip
                            title={listSubItem?.header}
                            placement="right"
                            overlayClassName={styles.templateTooltip}
                          >
                            <span
                              style={
                                width
                                  ? {
                                      maxWidth: `${width}px`
                                    }
                                  : {}
                              }
                              className={styles.templateName}
                            >
                              {listSubItem?.header}
                            </span>
                          </Tooltip>
                          {listSubItem?.subHeader && (
                            <span className={styles.templateCreator}>
                              {listSubItem?.subHeader}
                            </span>
                          )}
                        </div>
                        {shouldShowViewItemButton ? (
                          <Button
                            type="link"
                            className={styles.viewTemplateButton}
                            onClick={event => {
                              event.stopPropagation()
                              onViewItemClick?.(listSubItem?.id as string)
                            }}
                          >
                            {viewItemButtonText}
                          </Button>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              )
            )
          })}
        </div>
      </div>
      {shouldShowAddItemButton && (
        <Button
          className={styles.createTemplate}
          type="text"
          onClick={handleSubmitClick}
        >
          <div className={styles.iconContainer}>
            <div className={styles.plusIcon}>{plusImage}</div>
            <div>{submitButtonText}</div>
          </div>
        </Button>
      )}
    </div>
  )
}
